import React, { useEffect, useState, useRef, useCallback } from "react";
import { useUserWorklistQueryQuery } from "../../../../services/ticketEasyService/ticketEasy-api";
import { UserWorklistRequest } from "../../../../services/ticketEasyService/commands/UserWorklistRequest";
import { Loading } from "../../../shared";
import { useMyProfileQuery } from "../../../../services/profileService/profile-api";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { MessageDetail } from "./MessageDetail";
import { PriorityChip } from "../../components/PriorityChip";
import { StatusChip } from "../../components/StatusChip";
import { useLocation, useNavigate } from "react-router-dom";
import "../../TicketEasy.css";
import { Attachment } from "../../../../services/ticketEasyService/model/ticketEasy/ticketAttachments/Attachment";
import { setMessageViewType } from "../../../../app/slices/TicketEasyModuleSlice";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import {
  mapToBackendViewType,
  mapToFrontendViewType,
} from "../../utils/navigationUtils";

interface Approval {
  comments?: string;
  date?: string;
  approvedBy?: string;
}

interface Message {
  id: string;
  createdBy: string;
  createdDate: string;
  dueDate: string;
  description: string;
  status: string;
  priority: string;
  title: string;
  updatedBy?: string;
  comments?: any[];
  attachments?: Attachment[];
  UARFields?: any[];
  approvals?: Approval[];
}

interface MessageGridItem extends Message {
  index: number;
  expanded: boolean;
}

export const Messages: React.FC = () => {
  const selectedModule = useAppSelector(
    (state) => state.ticketEasyModule.TicketEasySelectedStartModule
  );
  const messageViewType = useAppSelector(
    (state) => state.ticketEasyModule.MessageViewType
  );
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: myProfile } = useMyProfileQuery();
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [data, setData] = useState<MessageGridItem[]>([]);
  const detailRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const listContainerRef = useRef<HTMLDivElement | null>(null); // Extract the query params from URL
  const queryParams = new URLSearchParams(location.search);
  const highlightedTicketId = queryParams.get("highlight");
  const viewTypeParam = queryParams.get("viewType");

  // Set view type from URL parameter if present
  useEffect(() => {
    if (viewTypeParam) {
      const validViewTypes = [
        "all",
        "created",
        "approver",
        "allMessages",
        "myMessages",
        "pendingMyApproval",
      ];
      
      // Redirect non-admins trying to access allMessages view
      if (viewTypeParam === "allMessages" && myProfile && !myProfile.isAdmin) {
        dispatch(setMessageViewType("myMessages"));
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("viewType", "myMessages");
        navigate({
          pathname: location.pathname,
          search: searchParams.toString(),
        }, { replace: true });
      } else if (validViewTypes.includes(viewTypeParam)) {
        dispatch(setMessageViewType(viewTypeParam as any));
      }
    }
  }, [viewTypeParam, dispatch, myProfile, navigate, location]);
  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue:
      | "all"
      | "created"
      | "approver"
      | "allMessages"
      | "myMessages"
      | "pendingMyApproval"
  ) => {
    // Prevent non-admins from accessing allMessages view
    if (newValue === "allMessages" && myProfile && !myProfile.isAdmin) {
      return;
    }
    
    dispatch(setMessageViewType(newValue));

    // Update URL with the new view type
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("viewType", newValue);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };
  const {
    data: messages,
    error,
    isLoading,
  } = useUserWorklistQueryQuery(
    new UserWorklistRequest(
      selectedModule,
      mapToBackendViewType(messageViewType),
      myProfile?.identity || "",
      myProfile?.email || ""
    ),
    { skip: !searchTriggered }
  );

  useEffect(() => {
    setSearchTriggered(true);
  }, []);

  useEffect(() => {
    if (messages) {
      setData(
        messages.map((message, index) => ({
          index: index,
          id: message.id,
          createdBy: message.createdBy,
          createdDate: message.createdDate,
          dueDate: message.dueDate,
          description: message.description,
          status: message.status,
          priority: message.priority,
          title: message.title,
          approvals: message.approvals,
          comments: message.comments,
          attachments: message.attachments,
          expanded: message.id === highlightedTicketId, // Auto-expand the highlighted ticket
        }))
      );
    }
  }, [messages, highlightedTicketId]);

  // Scroll to the highlighted ticket after data is loaded
  useEffect(() => {
    if (highlightedTicketId && data.length > 0) {
      const highlightedItem = data.find(
        (item) => item.id === highlightedTicketId
      );
      if (highlightedItem) {
        setTimeout(() => {
          const detailElement = detailRefs.current.get(highlightedItem.index);
          if (detailElement) {
            detailElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 150);
      }
    }
  }, [data, highlightedTicketId]);

  const handleItemClick = useCallback(
    (index: number) => {
      setData((prevData) =>
        prevData.map((item) =>
          item.index === index ? { ...item, expanded: !item.expanded } : item
        )
      );

      const clickedItem = data.find((item) => item.index === index);

      if (clickedItem) {
        // When clicking an item, update the URL to include the ticket ID as a highlight parameter
        // but maintain the current base path
        const searchParams = new URLSearchParams();
        searchParams.set("highlight", clickedItem.id);
        navigate({
          pathname: location.pathname,
          search: searchParams.toString(),
        });
      }

      setTimeout(() => {
        const detailElement = detailRefs.current.get(index);
        if (detailElement) {
          detailElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    },
    [data, navigate, location.pathname]
  );

  const setDetailRef = (index: number, element: HTMLDivElement | null) => {
    if (element) {
      detailRefs.current.set(index, element);
    }
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Message View Selector */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "#f8f9fa",
          px: 2,
          pt: 1,
        }}
      >
        {" "}
        <Tabs
          value={messageViewType}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {/* Only show All Messages tab for admins */}
          {myProfile?.isAdmin && (
            <Tab
              label={
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  All Messages
                </Typography>
              }
              value="allMessages"
              sx={{
                minHeight: 48,
                borderRadius: "8px 8px 0 0",
                "&.Mui-selected": {
                  backgroundColor: "white",
                  borderLeft: "1px solid #e0e0e0",
                  borderRight: "1px solid #e0e0e0",
                  borderTop: "1px solid #e0e0e0",
                  borderBottom: "none",
                },
                "&:hover": {
                  backgroundColor:
                    messageViewType === "allMessages" ? "white" : "#f1f3f5",
                },
                transition: "background-color 0.2s",
              }}
            />
          )}
          <Tab
            label={
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                My Messages
              </Typography>
            }
            value="myMessages"
            sx={{
              minHeight: 48,
              borderRadius: "8px 8px 0 0",
              "&.Mui-selected": {
                backgroundColor: "white",
                borderLeft: "1px solid #e0e0e0",
                borderRight: "1px solid #e0e0e0",
                borderTop: "1px solid #e0e0e0",
                borderBottom: "none",
              },
              "&:hover": {
                backgroundColor:
                  messageViewType === "myMessages" ? "white" : "#f1f3f5",
              },
              transition: "background-color 0.2s",
            }}
          />
          <Tab
            label={
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Pending My Approval
              </Typography>
            }
            value="pendingMyApproval"
            sx={{
              minHeight: 48,
              borderRadius: "8px 8px 0 0",
              "&.Mui-selected": {
                backgroundColor: "white",
                borderLeft: "1px solid #e0e0e0",
                borderRight: "1px solid #e0e0e0",
                borderTop: "1px solid #e0e0e0",
                borderBottom: "none",
              },
              "&:hover": {
                backgroundColor:
                  messageViewType === "pendingMyApproval" ? "white" : "#f1f3f5",
              },
              transition: "background-color 0.2s",
            }}
          />
        </Tabs>
      </Box>

      {isLoading && <Loading />}
      {error && (
        <div className="error-container">
          <p>Error loading messages</p>
          <p className="error-details">
            {(error as Error).message || "Please try again later"}
          </p>
        </div>
      )}
      {messages && (
        <div
          ref={listContainerRef}
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Header Row */}
          <div
            style={{
              display: "flex",
              padding: "10px",
              borderBottom: "1px solid #e0e0e0",
              fontWeight: "bold",
              backgroundColor: "#f5f5f5",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <div style={{ width: "120px" }}>Due Date</div>
            <div style={{ width: "120px" }}>Created Date</div>
            <div style={{ width: "150px" }}>Created By</div>
            <div style={{ flex: 1, maxWidth: "450px" }}>Description</div>
            <div style={{ width: "120px" }}>Status</div>
            <div style={{ width: "100px" }}>Priority</div>
            <div style={{ width: "100px" }}>Comments</div>
          </div>

          {data.length === 0 && !isLoading && (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#666",
                backgroundColor: "white",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="subtitle1">
                {messageViewType === "all"
                  ? "No messages found"
                  : messageViewType === "created"
                  ? "You have not created any messages"
                  : "No messages require your approval"}
              </Typography>
            </div>
          )}

          {/* Data Rows */}
          {data.map((item) => (
            <div key={item.id}>
              {/* Main Row */}
              <div
                style={{
                  display: "flex",
                  padding: "10px",
                  borderBottom: "1px solid #e0e0e0",
                  cursor: "pointer",
                  backgroundColor: item.expanded
                    ? "#f0f7ff"
                    : item.id === highlightedTicketId
                    ? "#fafad2"
                    : "white",
                }}
                onClick={() => handleItemClick(item.index)}
              >
                <div style={{ width: "120px" }}>{item.dueDate}</div>
                <div style={{ width: "120px" }}>{item.createdDate}</div>
                <div style={{ width: "150px" }}>{item.createdBy}</div>
                <div style={{ flex: 1, maxWidth: "450px" }}>
                  {item.description}
                </div>
                <div style={{ width: "120px" }}>
                  <StatusChip status={item.status} />
                </div>
                <div style={{ width: "100px" }}>
                  <PriorityChip priority={item.priority} />
                </div>{" "}
                <div style={{ width: "100px" }}>
                  {item.comments?.filter((comment) => !comment.isDeleted)
                    ?.length || 0}
                </div>
              </div>

              {/* Detail Row */}
              {item.expanded && (
                <div
                  ref={(el) => setDetailRef(item.index, el)}
                  style={{
                    width: "100%",
                    padding: "0",
                    backgroundColor: "transparent",
                  }}
                >
                  {" "}
                  <MessageDetail
                    dataItem={{
                      ...item,
                      id: item.id.toString(),
                      createdBy: item.createdBy,
                      createdDate: item.createdDate,
                      dueDate: item.dueDate,
                      description: item.description,
                      status: item.status,
                      priority: item.priority,
                      title: item.title,
                      approvals: item.approvals,
                      comments: item.comments,
                      attachments: item.attachments,
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
